import React, { useState } from 'react';
import { Card, Typography, Tag, Button } from 'antd';
import { EnvironmentOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { useTheme } from '../../theme/ThemeProvider';
import { getTripTypePersian, getTripStateText } from '../../utils/tripUtils';
import { Trip } from '../../types/trip';
import TripChatPopup from '../TripChatPopup';
import './styles.css';

const { Text } = Typography;

interface TripCardProps {
  trip: Trip;
  onClick?: (trip: Trip) => void;
  className?: string;
}

const TripCard: React.FC<TripCardProps> = ({ trip, onClick, className = '' }) => {
  const { theme } = useTheme();
  const stateInfo = getTripStateText(trip.state);
  const [isChatOpen, setIsChatOpen] = useState(false);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      
      // Convert to Persian (Shamsi) date
      const persianDate = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
        year: 'numeric',
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      }).formatToParts(date);
      
      // Extract Persian date components
      const year = persianDate.find(part => part.type === 'year')?.value || '';
      const dayOfWeek = persianDate.find(part => part.type === 'weekday')?.value || '';
      const dayOfMonth = persianDate.find(part => part.type === 'day')?.value || '';
      const month = persianDate.find(part => part.type === 'month')?.value || '';
      
      return `${year} ${dayOfWeek} ${dayOfMonth} ${month}`;
    } catch (error) {
      console.error('❌ Error formatting Persian date:', error);
      return 'تاریخ نامعتبر';
    }
  };

  const formatTime = (timeString: string) => {
    // Remove seconds, keep only hours and minutes (HH:mm)
    return timeString.substring(0, 5);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(trip);
    }
  };

  const handleChatClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when clicking chat button
    setIsChatOpen(true);
  };

  return (
    <>
      <Card 
        className={`trip-card ${className}`}
        hoverable={!!onClick}
        onClick={handleCardClick}
        style={{ 
          backgroundColor: theme.surface,
          borderColor: theme.border,
          color: theme.text.primary,
          cursor: onClick ? 'pointer' : 'default'
        }}
      >
      {/* Date, Time and Support Code at the top center */}
      <div className="trip-date-time-header">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          width: '100%'
        }}>
          <Text style={{ 
            color: theme.text.primary, 
            fontSize: '14px'
          }}>
            {formatDate(trip.startDate)} - {formatTime(trip.startTime)}
          </Text>
          {trip.supportCode && (
            <Tag color="green" style={{ fontSize: '12px' }}>
              کد: {trip.supportCode}
            </Tag>
          )}
        </div>
      </div>

      {/* Trip Status and Trip Type */}
      <div className="trip-status-section">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          width: '100%'
        }}>
          <Text style={{ 
            color: theme.text.secondary, 
            fontSize: '14px'
          }}>
            وضعیت سفر: {stateInfo.text}
          </Text>
          <Tag color="blue" style={{ fontSize: '12px' }}>
            {getTripTypePersian(trip.type)}
          </Tag>
        </div>
      </div>

      {/* Source and Destination */}
      <div className="trip-locations">
        <div className="location-item" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <EnvironmentOutlined style={{ color: theme.primary, fontSize: '16px', marginTop: 2 }} />
            <Text strong style={{ color: theme.text.primary, marginRight: 8, whiteSpace: 'nowrap' }}>
              مبدا:
            </Text>
            <Text style={{ 
              color: theme.text.secondary, 
              fontSize: '14px',
              textAlign: 'right',
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              flex: 1,
              minWidth: 0
            }}>
              {trip.source.address}
            </Text>
          </div>
        </div>

        <div className="location-item">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <EnvironmentOutlined style={{ color: theme.success, fontSize: '16px', marginTop: 2 }} />
            <Text strong style={{ color: theme.text.primary, marginRight: 8, whiteSpace: 'nowrap' }}>
              مقصد:
            </Text>
            <Text style={{ 
              color: theme.text.secondary, 
              fontSize: '14px',
              textAlign: 'right',
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              flex: 1,
              minWidth: 0
            }}>
              {trip.destination.address}
            </Text>
          </div>
        </div>
      </div>

      {/* Chat Button */}
      <div className="trip-chat-section">
        <Button
          type="primary"
          icon={<CustomerServiceOutlined />}
          onClick={handleChatClick}
          size="small"
          style={{
            backgroundColor: theme.primary,
            borderColor: theme.primary,
            borderRadius: '6px',
            fontSize: '12px',
            height: '32px',
            paddingLeft: '12px',
            paddingRight: '12px'
          }}
        >
          چت پشتیبانی
        </Button>
      </div>
    </Card>

      {/* Trip Chat Popup */}
      <TripChatPopup
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        tripId={trip.id}
        tripTitle={`سفر ${formatDate(trip.startDate)} - ${trip.source.address} به ${trip.destination.address}`}
      />
    </>
  );
};

export default TripCard;
